import type { PhoneItem } from "./phone";
import type { WorkingDaysFields } from "./working-days";

/* eslint-disable import/no-unused-modules */
export interface ThemeFields {
  theme: string;
}

export interface LocaleFields {
  language: string;
  timezone: string;
  locale: string;
}

export interface LocationPoint {
  type: string;
  coordinates: number[];
}

export interface LocationFields {
  location_country?: string;
  location_country_code?: string;
  location_state?: string;
  location_city?: string;
  location_zip?: string;
  location_address?: string;
  location_address2?: string;
  location_point?: LocationPoint;
  location_latitude?: string;
  location_longitude?: string;
  sys_root?: string;
}

export enum AccountType {
  BUSINESS = 1,
  NON_PROFIT = 2,
  GOVERNMENT = 3,
  INDIVIDUAL_OR_GROUP = 4,
}

export enum AccountIndustry {
  ACCOUNTING = 28,
  ADVERTISING_OR_MARKETING_AGENCY = 24,
  AGRICULTURE = 36,
  ART_CULTURE_AND_HUMANITIES = 14,
  AUTOMOTIVE = 37,
  CLUB_OTHER = 26,
  CLUB_SPORTS = 27,
  CONSTRUCTION = 29,
  CONSUMER = 1,
  EDUCATION = 2,
  ENERGY_AND_UTILITIES = 3,
  ENVIRONMENT_AND_ANIMALS = 15,
  FEDERAL_GOVERNMENT = 21,
  FINANCE = 4,
  FRANCHISE = 31,
  GOVERNMENT = 5,
  HEALTH_AND_HUMAN_SERVICES = 16,
  HEALTH_CARE = 6,
  INTERNATIONAL_OR_FOREIGN_AFFAIRS = 17,
  LOCAL_GOVERNMENT = 22,
  LAW = 30,
  MANUFACTURING = 7,
  MARKETING_AGENCY = 35,
  MATERIALS = 8,
  MEMBERSHIP = 18,
  NON_PROFIT = 9,
  NUTRITION = 38,
  PROFESSIONAL_SERVICES = 12,
  REAL_ESTATE = 32,
  RELIGIOUS = 19,
  RESTAURANT = 39,
  RETAIL = 33,
  SOCIAL_BENEFIT = 20,
  SPORTS = 25,
  STATE_GOVERNMENT = 23,
  TECHNOLOGY = 10,
  TELECOMMUNICATIONS = 34,
  TRANSPORTATION = 11,
  OTHER = 13,
}

export enum AccountSize {
  FROM_1_TO_10 = 1,
  FROM_11_TO_25 = 2,
  FROM_26_TO_100 = 3,
  FROM_101_TO_250 = 4,
  FROM_251_TO_500 = 5,
  FROM_501_TO_1000 = 6,
  FROM_1001_TO_5000 = 7,
  OVER_5000 = 8,
}

export interface InviteBase {
  id?: string;
  invited_to?: string | number;
  role: string;
  type: string;
  teams: string[];
  message?: string;
}

export interface Invite extends InviteBase {
  email: string;
  created_by: string;
  user_exists: boolean;
  invited_to_name: string;
}

export enum PlanCategory {
  Free = "free",
  Trial = "trial",
  Team = "team",
  Pro = "pro",
  Enterprise = "enterprise",
}

export interface AccountFeatures {
  app_permissions: boolean;
  field_permissions: boolean;
  lock_view: boolean;
  view_folders: boolean;
  sso: boolean;
  mfa: boolean;
}

export interface AccountPlan {
  id: string;
  category: PlanCategory;
  members_limit: number | null;
  number_of_solutions: number | null;
  allow_solutions_overage: boolean;
  records_per_solution: number | null;
  allow_records_overage: boolean;
  file_storage: number | null;
  allow_storage_overage: boolean;
  days_in_recycle_bin: number;
  features: AccountFeatures;
}

export type SubscriptionStatus =
  | "future"
  | "in_trial"
  | "paused"
  | "active"
  | "non_renewing"
  | "cancelled";

export enum MemberDirectoryFilterTypes {
  ALL = 1,
  TEAM = 2,
  EMAIL_DOMAIN = 3,
}

export interface AccountMemberDirectoryFilter {
  type: MemberDirectoryFilterTypes;
  included_members: string[];
}

export interface AccountSettings {
  member_directory_filter: AccountMemberDirectoryFilter;
  filestack_api_key: string;
}

export interface Workspace
  extends ThemeFields,
    LocaleFields,
    LocationFields,
    PhoneItem,
    WorkingDaysFields {
  id: string;
  slug: string;
  name: string;
  owner: number;
  status: number;
  website: string;
  type: AccountType;
  industry: AccountIndustry;
  size: AccountSize;
  is_personalization_supported: boolean;
  subscription_id: string;
  created_on: string;
  updated_on: string;
  logo?: File;
  invites?: Invite[];
  imageURL?: string;
  areas_of_interest?: string[];

  // Account's plan information
  plan: AccountPlan;
  licensed_users: number;
  subscription_status: SubscriptionStatus;
  subscription_trial: boolean;
  subscription_start: string;
  subscription_end: string;

  settings: AccountSettings;
}
